import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BrandLogo, BrandMark } from "./BrandLogo";
import api from "../lib/api";
import {
  LayoutDashboard, MessagesSquare, Users, Target, Bot, BookOpen,
  Settings as SettingsIcon, LogOut, Phone, Workflow, ShieldAlert,
  Images, Menu, X, Megaphone, Tag, MessageSquareText,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inbox", label: "WhatsApp Inbox", icon: MessagesSquare, testid: "nav-inbox" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/leads", label: "Leads", icon: Target, testid: "nav-leads" },
  { to: "/broadcasts", label: "Broadcasts", icon: Megaphone, testid: "nav-broadcasts" },
  { to: "/offers", label: "Offers", icon: Tag, testid: "nav-offers" },
  { to: "/saved-replies", label: "Saved Replies", icon: MessageSquareText, testid: "nav-saved-replies" },
  { to: "/automations", label: "Automations", icon: Workflow, testid: "nav-automations" },
  { to: "/voice", label: "Voice Calls", icon: Phone, testid: "nav-voice" },
  { to: "/ai-agent", label: "AI Agent", icon: Bot, testid: "nav-ai-agent" },
  { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/assets", label: "Asset Library", icon: Images, testid: "nav-assets" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const SOON = [];

function statusColor(status) {
  if (status === "LIVE") return "bg-[#25D366]";
  if (status === "CONFIGURED") return "bg-amber-500";
  if (status === "ERROR") return "bg-red-500";
  return "bg-[#8696A0]";
}
function statusLabel(status) {
  if (status === "LIVE") return "LIVE";
  if (status === "CONFIGURED") return "ACTION REQUIRED";
  if (status === "ERROR") return "ERROR";
  return "NOT CONNECTED";
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [waStatus, setWaStatus] = useState("NOT_CONNECTED");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const h = () => setMobileOpen(true);
    window.addEventListener("open-sidebar", h);
    return () => window.removeEventListener("open-sidebar", h);
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get("/whatsapp/status");
        if (mounted) setWaStatus(res.data.status || "NOT_CONNECTED");
      } catch (e) {}
    };
    load();
    const id = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(id); };
  }, [location.pathname]);

  return (
    <div className="h-[100dvh] overflow-hidden flex bg-[#F0F2F5] text-[#111B21]">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`w-64 flex-shrink-0 border-r border-[#E9EDEF] bg-white flex flex-col fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center justify-between gap-2 px-5 bg-[#075E54]">
          <BrandLogo markSize={32} textClass="text-white" />
          <button className="lg:hidden text-white/70 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="sidebar-close"><X className="h-5 w-5" /></button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive || (["/settings", "/voice"].includes(item.to) && location.pathname.startsWith(item.to))
                    ? "bg-[#E7FCE8] text-[#075E54] font-semibold border-l-4 border-[#25D366] rounded-l-md"
                    : "text-[#54656F] hover:bg-[#F0F2F5] hover:text-[#111B21] font-medium"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive || location.pathname.startsWith("/admin")
                    ? "bg-amber-50 text-amber-700 font-semibold border-l-4 border-amber-500 rounded-l-md"
                    : "text-amber-600 hover:bg-amber-50 font-medium"
                }`
              }
            >
              <ShieldAlert className="h-4 w-4" />
              Admin Panel
            </NavLink>
          )}

          {SOON.length > 0 && <div className="pt-5 pb-2 px-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8696A0]">Coming soon</p>
          </div>}
          {SOON.map((item) => (
            <div key={item.to} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#8696A0] cursor-not-allowed">
              <item.icon className="h-4 w-4" />
              {item.label}
              <span className="ml-auto text-[9px] font-mono border border-[#E9EDEF] rounded-md px-1.5 py-0.5 text-[#8696A0]">soon</span>
            </div>
          ))}
        </nav>

        {/* Connection health */}
        <div className="px-3 py-3 border-t border-[#E9EDEF]">
          <div data-testid="connection-health" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F0F2F5] border border-[#E9EDEF]">
            <span className={`h-2 w-2 rounded-full ${statusColor(waStatus)} ${waStatus === "LIVE" ? "animate-glow" : ""}`} />
            <span className="text-xs text-[#54656F]">WhatsApp</span>
            <span className="ml-auto font-mono text-[10px] text-[#111B21]">{statusLabel(waStatus)}</span>
          </div>
        </div>

        {/* User */}
        <div className="px-3 py-3 border-t border-[#E9EDEF] flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-[#25D366] flex items-center justify-center text-sm font-semibold text-white">{user?.name?.[0]}</div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-[#111B21] truncate">{user?.name}</p>
            <p className="text-[10px] text-[#8696A0] truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-button" onClick={logout} className="text-[#8696A0] hover:text-red-500 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        {/* Mobile top bar (hidden on Inbox — it provides its own header) */}
        <div className={`${location.pathname === "/inbox" ? "hidden" : "flex lg:hidden"} h-14 items-center gap-3 px-4 bg-[#075E54] sticky top-0 z-20`}>
          <button onClick={() => setMobileOpen(true)} data-testid="sidebar-open" className="text-white/90 hover:text-white">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <BrandMark size={26} />
            <span className="font-heading text-sm font-extrabold text-white">MuviArts.Online</span>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
